import { useCallback } from 'react'
import { useStatuses } from './useWorkspaceStatuses'
import type { Stage } from '@/lib/utils/status-stage'
import type { StatusDocument } from '@/lib/db/rxdb/schemas/status'
import { stageForProgressType } from '@/lib/utils/status-stage'
import { isReservedStatusName } from '@/routes/features/settings/status/constants/reservedStatusNames'
import { compareRanks } from '@/lib/utils/lexorank'

const stageMaps = new WeakMap<ReadonlyArray<StatusDocument>, Map<string, Stage>>()

/**
 * Stage of every status in a workspace (design 31a). Human in-progress statuses split
 * in workflow rank: the first half reads "active" (half ring), the rest "review"
 * (¾ ring). AI-lane statuses stay "active". Cached per statuses array, so every glyph
 * on a board shares one map.
 */
export function buildStageMap(statuses: ReadonlyArray<StatusDocument>): Map<string, Stage> {
  const cached = stageMaps.get(statuses)
  if (cached) return cached

  const isHumanActive = (s: StatusDocument) =>
    s.progressType.toUpperCase() === 'IP' && !isReservedStatusName(s.name)
  // Split each workspace's own active statuses by workflow rank, so the answer
  // doesn't depend on the order (or scope) of the list a caller passes in.
  const activeByWorkspace = new Map<string, Array<StatusDocument>>()
  for (const status of statuses) {
    if (status.isDeleted || !isHumanActive(status)) continue
    const list = activeByWorkspace.get(status.envId) ?? []
    list.push(status)
    activeByWorkspace.set(status.envId, list)
  }
  const map = new Map<string, Stage>()
  for (const list of activeByWorkspace.values()) {
    list.sort((a, b) => compareRanks(a.sortOrder, b.sortOrder))
    list.forEach((status, index) => {
      map.set(status.id, stageForProgressType('IP', index, list.length))
    })
  }
  for (const status of statuses) {
    if (!map.has(status.id)) map.set(status.id, stageForProgressType(status.progressType))
  }
  stageMaps.set(statuses, map)
  return map
}

/**
 * Resolve many statuses' stages from one subscription — for views that colour every
 * task (calendar, Gantt). Unknown ids fall back to the progress type.
 */
export function useStageOf(): (
  statusId: string | null | undefined,
  progressType: string | null | undefined,
) => Stage {
  const { all } = useStatuses()
  return useCallback(
    (statusId, progressType) =>
      (statusId ? buildStageMap(all.statuses).get(statusId) : undefined) ??
      stageForProgressType(progressType),
    [all.statuses],
  )
}

/** A status's stage; falls back to its progress type when the id isn't known. */
export function useStatusStage(
  statusId: string | null | undefined,
  progressType: string | null | undefined,
): Stage {
  return useStageOf()(statusId, progressType)
}
